import {
	Dispatch,
	ReactElement,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { GenericNavigationPanelProps, SubNavigation } from "../SubNavigation";
import { localize } from "client_legacy/ndaw/culture";
import { useShouldStartLoadingData } from "./useShouldStartLoadingData";
import { AppType } from "client/Api/Apps/common";
import { getHubUrlForPage } from "client/Features/Hub/HubOverviewPage";
import { getHubUrlForCommunity } from "client/Features/Hub/Communities/CommunitiesPage";
import { getHubUrlForEvent } from "client/Features/Hub/Events/EventsPage";
import { getHubUrlForKnowledgeItem } from "client/Features/Hub/KnowledgeItems/KnowledgeItemsPage";
import { getHubUrlForAnnouncement } from "client/Features/Hub/Announcements/AnnouncementsPage";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	getHierarchy,
	HubDataSource,
	RootHubSidebarNodeModel
} from "client/Api/Hub/hub";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { NoResults } from "client/Components";
import { useForceRerender } from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { never } from "client_legacy/ndaw/errors";
import {
	convertNodeModelsToSidebarNodes,
	HubRootNode,
	HubSidebar,
	HubSidebarValue
} from "client/Features/Hub/Sidebar/HubSidebar";
import {
	HubEvent,
	subscribe as subscribeHubItemEvent,
} from "client/Features/Hub/events";
import {
	CommunityEvent,
	subscribe as subscribeCommunityEvent,
} from "client/Features/Hub/Communities/events";
import {
	HubSideBarNewItemReductionTarget,
	reduceHubSidebarNewItemCountByOne
} from "client/Features/Hub/HubLayout";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";

type HubPanelProps = GenericNavigationPanelProps;

export const HubPanel = (props: HubPanelProps): ReactElement | null => {
	const {
		isVisible,
		isPinned,
		isActive
	} = props;

	const [hierarchy, setHierarchy] = useState<readonly RootHubSidebarNodeModel[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hasError, setHasError] = useState<boolean>(false);
	const [reload, hubCategoriesModelVersion] = useForceRerender();

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);
	const isRenderedByPage: boolean = isActive;

	const rootNodes = useMemo(
		(): readonly HubRootNode[] => convertNodeModelsToSidebarNodes(hierarchy),
		[hierarchy]
	);

	usePubSubEvents(setHierarchy);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(hubCategoriesModelVersion);

			if (!shouldStartLoadingData || isRenderedByPage) {
				return;
			}

			async function fetchItems(signal: AbortSignal): Promise<void> {
				try {
					setHasError(false);
					setIsLoading(true);

					const hierarchy: readonly RootHubSidebarNodeModel[] = await getHierarchy(signal);

					setHierarchy(hierarchy);
					setIsLoading(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}
					setHasError(true);
					setIsLoading(false);
				}
			}

			const cancellation = new AbortController();

			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			hubCategoriesModelVersion,
			shouldStartLoadingData,
			isRenderedByPage
		]
	);

	const handleNavigation = useCallback(
		(value: HubSidebarValue): void => {
			let url: string;

			switch (value.source) {
				case undefined:
					url = getHubUrlForPage();
					break;
				case HubDataSource.Announcement:
					url = getHubUrlForAnnouncement({
						source: HubDataSource.Announcement,
						categoryId: typeof value.categoryId === "number"
							? value.categoryId
							: undefined,
						itemId: undefined
					});
					break;
				case HubDataSource.Event:
					url = getHubUrlForEvent({
						source: HubDataSource.Event,
						categoryId: typeof value.categoryId === "number"
							? value.categoryId
							: undefined,
						itemId: undefined
					});
					break;
				case HubDataSource.KnowledgeItem:
					url = getHubUrlForKnowledgeItem({
						source: HubDataSource.KnowledgeItem,
						viewIdentifier: value.viewIdentifier
					});
					break;
				case HubDataSource.Community:
					url = getHubUrlForCommunity({
						source: HubDataSource.Community,
						communityId: value.communityId
					});
					break;
				default:
					never(value);
			}

			navigateTo(url);
		},
		[]
	);

	if (isRenderedByPage) {
		return null;
	}

	const renderContent = (): ReactElement => {
		if (isLoading || !shouldStartLoadingData) {
			return <SubNavigation.LoadingIndicator />;
		}

		return hasError
			? (
				<NoResults
					title={localize("_UnableToRetrieveItems")}
					icon="gear"
					message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
					onRetry={reload}
					height="fill"
				/>
			)
			: (
				<HubSidebar
					rootNodes={rootNodes}
					onChange={handleNavigation}
					value={undefined}
					activeFilter={null}
					areActionsAvailable={false}
				/>
			);
	};

	return (
		<>
			<SubNavigation.Header
				title={localize("_TheHub")}
				kind={AppType.Hub}
			/>
			<SubNavigation.Body>
				{renderContent()}
			</SubNavigation.Body>
		</>
	);
};

HubPanel.displayName="HubPanel";

function usePubSubEvents(
	setHierarchy: Dispatch<SetStateAction<readonly RootHubSidebarNodeModel[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				setHierarchy
			);

			return (): void => subscriptionTokens.destroy();
		},
		[setHierarchy]
	);
}

function registerSubscriptions(
	setHierarchy: Dispatch<SetStateAction<readonly RootHubSidebarNodeModel[]>>
): SubscriptionTokenCollection {
	const result: SubscriptionTokenCollection = new SubscriptionTokenCollection();

	const isNewStatusClearedToken: PubSubSubscriptionToken = subscribeHubItemEvent(
		HubEvent.IsNewStatusCleared,
		(_, data) => {
			let target: HubSideBarNewItemReductionTarget;

			switch (data.kind) {
				case HubDataSource.Community:
					target = {
						itemKind: HubDataSource.Community,
						communityId: data.communityId
					};
					break;
				case HubDataSource.Announcement:
					target = {
						itemKind: data.kind,
						categoryIds: data.categoryIds
					};
					break;
				case HubDataSource.KnowledgeItem:
				case HubDataSource.Event:
					target = {
						itemKind: data.kind,
						categoryIds: [data.categoryId]
					};
					break;
				default:
					never(data);
			}
			setHierarchy(
				rootItems => reduceHubSidebarNewItemCountByOne(
					rootItems,
					target
				)
			);
		}
	);

	result.add(isNewStatusClearedToken);

	const communityCreatedToken: PubSubSubscriptionToken = subscribeCommunityEvent(
		CommunityEvent.CommunityCreated,
		(_, data) => {
			if (data.isMember) {
				return;
			}

			setHierarchy(
				(rootItems): readonly RootHubSidebarNodeModel[] => addHubSidebarNewCommunityId(
					rootItems,
					data.id
				)
			);
		}
	);

	result.add(communityCreatedToken);

	return result;
}

export function addHubSidebarNewCommunityId(
	rootItems: readonly RootHubSidebarNodeModel[],
	communityId: number
): readonly RootHubSidebarNodeModel[] {
	return rootItems
		.map(
			(rootItem): RootHubSidebarNodeModel => {
				if (rootItem.source !== HubDataSource.Community) {
					return rootItem;
				}

				if (rootItem.newCommunityIds.includes(communityId)) {
					return rootItem;
				}

				return {
					...rootItem,
					newCommunityIds: [
						...rootItem.newCommunityIds,
						communityId
					]
				};
			}
		);
}